import type { Item } from '@/stores/canvasStore';
import { isTweetItem } from '@/components/items/TweetPreviewCard';
import { getLinkUrl } from '@/lib/linkUtils';
import { decodeHtmlEntities } from '@/lib/htmlEntities';

/**
 * Title, thumbnail and searchable text for items shown in Partnr search
 * results. X bookmarks are `link` items whose content is a tweet payload;
 * rows synced before PR #101 have no `title` or `url`, so they need their own
 * handling instead of falling back to "Link".
 */

const TWEET_TITLE_TEXT_LENGTH = 80;

interface TweetAuthor {
  name?: string;
  username?: string;
  profile_image_url?: string;
}

interface TweetMedia {
  type?: string;
  url?: string;
  preview_url?: string;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function getTweetAuthor(content: Record<string, unknown>): TweetAuthor {
  const author = content.author;
  return author && typeof author === 'object' ? (author as TweetAuthor) : {};
}

/** Author of a tweet item; fields are missing on partial rows. */
export function getTweetItemAuthor(item: Item): TweetAuthor {
  return getTweetAuthor((item.content || {}) as Record<string, unknown>);
}

/** First media image of a tweet item (url or preview_url), or null. */
export function getTweetMediaImage(item: Item): string | null {
  const content = (item.content || {}) as Record<string, unknown>;
  const media = Array.isArray(content.media) ? (content.media as TweetMedia[]) : [];
  for (const m of media) {
    const src = asString(m?.url) || asString(m?.preview_url);
    if (src) return src;
  }
  return null;
}

/** "@username: first ~80 chars of text" for a tweet item. */
export function getTweetTitle(item: Item): string {
  const content = (item.content || {}) as Record<string, unknown>;
  const author = getTweetAuthor(content);
  const text = asString(content.text).replace(/\s+/g, ' ').trim();
  const snippet = text.length > TWEET_TITLE_TEXT_LENGTH
    ? `${text.slice(0, TWEET_TITLE_TEXT_LENGTH).trimEnd()}…`
    : text;
  const handle = author.username ? `@${author.username}` : asString(author.name);

  if (handle && snippet) return `${handle}: ${snippet}`;
  return handle || snippet || 'Post on X';
}

/** Display title for a `link` search result (tweets included). */
export function getLinkResultTitle(item: Item): string {
  if (isTweetItem(item)) return getTweetTitle(item);
  const content = (item.content || {}) as Record<string, unknown>;
  return decodeHtmlEntities(asString(content.title)) || getLinkUrl(content) || 'Link';
}

/**
 * Thumbnail URL for a search result, or null to show the type icon.
 * Tweets: first media image (url or preview_url), else the author's avatar.
 * Other links: their OG image.
 */
export function getSearchResultThumbnail(item: Item): string | null {
  const content = (item.content || {}) as Record<string, unknown>;
  if (item.item_type === 'image') return asString(content.url) || null;
  if (item.item_type !== 'link') return null;

  if (isTweetItem(item)) {
    return getTweetMediaImage(item) || asString(getTweetAuthor(content).profile_image_url) || null;
  }

  return asString(content.image) || null;
}

/**
 * Lower-cased text fields used by search scoring. Index positions matter to
 * the scorer: 1 is the title, 3 the filename (both get a bonus).
 */
export function getSearchableFields(item: Pick<Item, 'item_type' | 'content'>): string[] {
  const content = (item.content || {}) as Record<string, unknown>;
  const author = getTweetAuthor(content);
  const isTweet = item.item_type === 'link' && !!content.tweet_id;

  return [
    asString(content.text),
    asString(content.title) || (isTweet ? getTweetTitle(item as Item) : ''),
    asString(content.body).replace(/<[^>]*>/g, ''), // Strip HTML
    asString(content.filename),
    asString(content.alt),
    asString(content.description),
    asString(content.url),
    asString(content.siteName),
    asString(content.aiDescription),
    asString(content.analysis),
    asString(author.name),
    asString(author.username),
  ].map(f => f.toLowerCase());
}
